import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import QRCodeDisplay from '../components/luggage/QRCodeDisplay';

export default function LuggageTagsPrint() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [qrItems, setQrItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Print options
  const [tagsPerPage, setTagsPerPage] = useState(4);
  const [qrSize, setQrSize] = useState(150);
  const [customSize, setCustomSize] = useState('');
  const [sizeMode, setSizeMode] = useState('preset'); // 'preset' or 'custom'
  
  // Get selected item IDs from URL params
  const selectedIds = searchParams.get('ids') ? searchParams.get('ids').split(',') : [];

  useEffect(() => {
    fetchQRItems();
  }, []);

  const fetchQRItems = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await apiCall("/api/qrItems?action=" + ApiActions.GET_QR_ITEMS, {}, 'POST');
      
      if (response.success) {
        // Filter to only selected items if IDs provided, otherwise show all
        const items = selectedIds.length > 0
          ? response.qrItems.filter(item => selectedIds.includes(item.id))
          : response.qrItems;
        setQrItems(items);
      } else {
        setError(response.message || t('luggageTags.print.failedToLoad'));
      }
    } catch (err) {
      console.error('Error fetching QR items:', err);
      setError(t('luggageTags.print.errorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Calculate grid layout based on tags per page
  const getGridCols = () => {
    switch (tagsPerPage) {
      case 1: return 12; // 1 per row (full width)
      case 2: return 6;  // 2 per row
      case 4: return 6;  // 2x2 grid (2 columns)
      case 6: return 4;  // 3x2 grid (3 columns)
      case 9: return 4;  // 3x3 grid (3 columns)
      default: return 6;
    }
  };

  // Calculate QR size based on selection
  const getQrSize = () => {
    if (sizeMode === 'custom' && customSize) {
      const size = parseInt(customSize);
      return isNaN(size) || size < 50 || size > 500 ? 150 : size;
    }
    switch (qrSize) {
      case 100: return 100; // Small
      case 150: return 150; // Medium
      case 200: return 200; // Large
      default: return 150;
    }
  };

  // Create array with the selected tag repeated based on tagsPerPage
  // Since only one tag is selected, we duplicate it to fill the page
  const tagsForPrint = qrItems.length > 0 
    ? Array(tagsPerPage).fill(qrItems[0]) 
    : [];
  
  // Group items into pages (for now, just one page with repeated tags)
  const pages = tagsForPrint.length > 0 ? [tagsForPrint] : [];

  return (
    <div className="luggage-tags-print-page">
      {/* Print options (visible only on screen, not in print) */}
      <div className="screen-only print-options-container">
        <Container className="py-4">
          <div className="mb-3">
            <h4 className="mb-0">{t('luggageTags.print.title')}</h4>
            <small className="text-muted">{t('luggageTags.print.subtitle')}</small>
          </div>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-semibold mb-2">{t('luggageTags.print.tagsPerPage')}</Form.Label>
              <Form.Select 
                value={tagsPerPage} 
                onChange={(e) => setTagsPerPage(parseInt(e.target.value))}
              >
                <option value={1}>{t('luggageTags.print.oneTag')}</option>
                <option value={2}>{t('luggageTags.print.twoTags')}</option>
                <option value={4}>{t('luggageTags.print.fourTags')}</option>
                <option value={6}>{t('luggageTags.print.sixTags')}</option>
                <option value={9}>{t('luggageTags.print.nineTags')}</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold mb-2">{t('luggageTags.print.qrCodeSize')}</Form.Label>
              <Form.Select 
                value={sizeMode}
                onChange={(e) => {
                  setSizeMode(e.target.value);
                  if (e.target.value === 'preset') {
                    setQrSize(150);
                  }
                }}
                className="mb-2"
              >
                <option value="preset">{t('luggageTags.print.presetSizes')}</option>
                <option value="custom">{t('luggageTags.print.customSize')}</option>
              </Form.Select>
              {sizeMode === 'preset' ? (
                <Form.Select 
                  value={qrSize} 
                  onChange={(e) => setQrSize(parseInt(e.target.value))}
                >
                  <option value={100}>{t('luggageTags.print.small')}</option>
                  <option value={150}>{t('luggageTags.print.medium')}</option>
                  <option value={200}>{t('luggageTags.print.large')}</option>
                </Form.Select>
              ) : (
                <Form.Control
                  type="number"
                  placeholder={t('luggageTags.print.enterSize')}
                  value={customSize}
                  onChange={(e) => setCustomSize(e.target.value)}
                  min={50}
                  max={500}
                />
              )}
            </Col>
            <Col md={5} className="text-end">
              <div className="d-flex gap-2 justify-content-end">
                <Button variant="outline-secondary" onClick={() => navigate('/luggage-tags')}>
                  <i className="bi bi-arrow-left me-2"></i>{t('luggageTags.print.back')}
                </Button>
                <Button variant="primary" onClick={handlePrint}>
                  <i className="bi bi-printer me-2"></i>{t('luggageTags.print.print')}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Print content */}
      <Container fluid className="print-container">
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('luggageTags.print.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('luggageTags.print.loadingTags')}</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : qrItems.length === 0 ? (
          <Alert variant="warning">{t('luggageTags.print.noTagsSelected')}</Alert>
        ) : (
          pages.map((pageItems, pageIndex) => (
            <div key={pageIndex} className="print-page">
              <Row className="g-2 justify-content-center" style={{ maxWidth: 'fit-content', margin: '0 auto' }}>
                {pageItems.map((item, index) => (
                  <Col 
                    key={`${item.id}-${index}`}
                    xs={12} 
                    sm={getGridCols()}
                    className="d-flex justify-content-center align-items-center"
                  >
                    <div className="qr-tag-print-item">
                      <QRCodeDisplay
                        name={item.name}
                        email={item.email}
                        phone={item.phone}
                        profileLink={item.profileLink}
                        headerText={item.headerText || "I'm Lost"}
                        footerText={item.footerText || 'Scan Me'}
                        format={item.format || 'simple'}
                        size={getQrSize()}
                        displayName={item.displayName}
                        showDisplayInfo={true}
                        includeWhatsApp={item.includeWhatsApp !== undefined ? item.includeWhatsApp : true}
                      />
                    </div>
                  </Col>
                ))}
              </Row>
              {/* Page break after each page except the last */}
              {pageIndex < pages.length - 1 && (
                <div className="page-break-after"></div>
              )}
            </div>
          ))
        )}
      </Container>
    </div>
  );
}
